import { useState } from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';

import { VoiceProvider } from '@/context/VoiceProvider';

import { useAgentTemplatesQuery, useCreateAgentMutation } from '@/query/agent.query';

import { CreateAgentFromTemplate } from './agent-template';
import { CreateAgentForm } from './create-agent-from-scratch';
import { AgentCreatePageNextButton } from './next-button';

import { AgentCreateFormProvider } from '@/formik/agent.formik';

interface CreateAgentDialogProps {
  toggle: () => void;
}

export function CreateAgentDialog({ toggle }: CreateAgentDialogProps) {
  const [step, setStep] = useState(1);
  const navigate = useNavigate();

  const { data: templates = [], isLoading: isLoadingTemplates } = useAgentTemplatesQuery();

  const handleClose = () => {
    toggle();
    setStep(1);
  };

  const handleNext = () => {
    if (step === 1) {
      setStep(2); // Go to final details step
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    }
  };

  const mutation = useCreateAgentMutation({
    onSuccess: agentId => {
      navigate(`/agents/${agentId}`);
    },
  });

  return (
    <Dialog open={true} onOpenChange={toggle}>
      <DialogContent
        className="!max-w-none !w-screen !h-screen p-0 border-0 bg-background flex items-center justify-center !fixed !inset-0 !translate-x-0 !translate-y-0 rounded-none !top-0 !left-0"
        showCloseButton={false}
      >
        {/* Close button */}
        <Button
          variant="outline"
          size="icon"
          className="absolute top-5 right-5 h-9 w-9 rounded-full z-10"
          onClick={handleClose}
        >
          <X className="h-[18px] w-[18px] opacity-70" />
        </Button>

        {/* Main content */}
        <div className="flex flex-col items-center justify-center gap-6 w-full max-w-[642px] px-5">
          <AgentCreateFormProvider onSubmit={mutation.mutate}>
            {/* Step 1: Combined Template Selection */}
            {step === 1 && (
              <>
                <div className="flex flex-col gap-1 w-full">
                  <div className="flex items-center w-full">
                    <p className="font-semibold text-xl leading-7 text-foreground">
                      Create an agent
                    </p>
                  </div>
                  <div className="flex items-center w-full">
                    <p className="font-normal text-sm leading-5 text-muted-foreground">
                      Select one of the options below to get started
                    </p>
                  </div>
                </div>

                <CreateAgentFromTemplate templates={templates} isLoading={isLoadingTemplates} />

                <AgentCreatePageNextButton
                  step={step}
                  handleNext={handleNext}
                  templates={templates}
                />
              </>
            )}

            {/* Step 2: Complete Agent Details */}
            {step === 2 && (
              <VoiceProvider>
                <CreateAgentForm handleBack={handleBack} isPending={mutation.isPending} />
              </VoiceProvider>
            )}
          </AgentCreateFormProvider>

          {/* Pagination dots */}
          <div className="flex items-center gap-2">
            {[1, 2].map(dotStep => (
              <div
                key={dotStep}
                className={`rounded-full ${
                  step === dotStep
                    ? 'w-5 h-1.5 bg-primary opacity-100'
                    : 'w-1.5 h-1.5 bg-foreground opacity-20'
                }`}
              />
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
